import { ApiClient, ChatMessage } from './api/client';
import { ChatUI, StreamingMessage } from './ui/chat';
import { createStyles } from './ui/styles';

export interface WidgetOptions {
  apiUrl: string;
  widgetId: string;
}

// How long a visitor can ignore the widget before we nudge them with a
// notification that the chat is available.
const INACTIVITY_DELAY_MS = 2 * 60 * 1000; // 2 minutes

const NOTIFICATION_TEXT = 'Have a question about the API? Ask me here.';

export class Widget {
  private container: HTMLElement;
  private apiClient: ApiClient;
  private chatUI: ChatUI | null = null;
  private conversationHistory: ChatMessage[] = [];
  private isInitialized = false;
  private notification: HTMLElement | null = null;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private hasInteracted = false;

  constructor(options: WidgetOptions) {
    this.apiClient = new ApiClient(options.apiUrl, options.widgetId);
    this.container = this.createContainer();
    document.body.appendChild(this.container);
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'apidocs-widget';
    container.innerHTML = `
      <button class="apidocs-widget-button" aria-label="Open chat">
        <svg viewBox="0 0 24 24">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
          <path d="M7 9h10v2H7zm0-3h10v2H7z"/>
        </svg>
      </button>
    `;
    return container;
  }

  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      const config = await this.apiClient.getConfig();

      // Inject styles
      const styleEl = document.createElement('style');
      styleEl.textContent = createStyles(config.theme);
      document.head.appendChild(styleEl);

      // Create chat UI
      this.chatUI = new ChatUI(this.container, config, {
        onSendMessage: (message) => this.handleSendMessage(message),
        onClose: () => this.chatUI?.close(),
      });

      // Setup toggle button. Clicking it counts as interacting, so cancel any
      // pending inactivity nudge.
      const toggleButton = this.container.querySelector(
        '.apidocs-widget-button'
      )!;
      toggleButton.addEventListener('click', () => {
        this.dismissNotification();
        this.chatUI?.toggle();
      });

      // Nudge the visitor if they haven't opened the chat within the delay.
      this.inactivityTimer = setTimeout(
        () => this.showNotification(),
        INACTIVITY_DELAY_MS
      );

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize widget:', error);
    }
  }

  // Builds the inactivity notification bubble, wires its handlers, and returns
  // it hidden (made visible later via the `visible` class).
  private createNotification(): HTMLElement {
    const notification = document.createElement('div');
    notification.className = 'apidocs-widget-notification';
    notification.setAttribute('role', 'button');
    notification.setAttribute('tabindex', '0');
    notification.innerHTML = `
      <button class="apidocs-widget-notification-close" aria-label="Dismiss">&times;</button>
      <span class="apidocs-widget-notification-text"></span>
    `;
    notification.querySelector('.apidocs-widget-notification-text')!.textContent =
      NOTIFICATION_TEXT;

    // Clicking the bubble opens the chat.
    notification.addEventListener('click', () => this.open());

    // The close button only dismisses the nudge without opening the chat.
    const closeButton = notification.querySelector(
      '.apidocs-widget-notification-close'
    )!;
    closeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.dismissNotification();
    });

    this.container.appendChild(notification);
    return notification;
  }

  private showNotification(): void {
    if (this.hasInteracted) return;
    if (!this.notification) {
      this.notification = this.createNotification();
    }
    // Defer adding the class so the transition runs from the hidden state.
    requestAnimationFrame(() => this.notification?.classList.add('visible'));
  }

  // Cancels the pending timer and hides the notification. Idempotent; also marks
  // the visitor as having interacted so the nudge won't reappear.
  private dismissNotification(): void {
    this.hasInteracted = true;
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.notification?.classList.remove('visible');
  }

  private async handleSendMessage(message: string): Promise<void> {
    if (!this.chatUI) return;
    const chatUI = this.chatUI;

    // Add user message to UI
    chatUI.addMessage({ role: 'user', content: message });
    chatUI.setInputEnabled(false);
    chatUI.showLoading();

    let assistant: StreamingMessage | null = null;
    let fullText = '';

    // Lazily create the assistant bubble on first use, replacing the loading
    // indicator only once there is something to show. Idempotent.
    const ensureBubble = (): StreamingMessage => {
      if (!assistant) {
        chatUI.hideLoading();
        assistant = chatUI.startAssistantMessage();
      }
      return assistant;
    };

    try {
      await this.apiClient.sendMessage(message, this.conversationHistory, {
        onDelta: (text) => {
          fullText += text;
          ensureBubble().update(fullText);
        },
        onDone: (sources) => {
          ensureBubble().setSources(sources);
        },
      });

      // Update conversation history with the completed exchange.
      this.conversationHistory.push({ role: 'user', content: message });
      this.conversationHistory.push({ role: 'assistant', content: fullText });
    } catch (error) {
      console.error('Failed to send message:', error);
      ensureBubble().update(
        fullText + '\n\n_Sorry, something went wrong. Please try again._'
      );
    } finally {
      chatUI.setInputEnabled(true);
    }
  }

  open(): void {
    // Opening the chat (via the launcher, the notification, or programmatically)
    // counts as interacting, so cancel any pending nudge.
    this.dismissNotification();
    this.chatUI?.open();
  }

  close(): void {
    this.chatUI?.close();
  }

  destroy(): void {
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.container.remove();
  }
}
